import { AxiosResponse } from 'axios'
import { http } from '../realisations/Http.realisation'
import {
    AcceptAdmissionPayload,
    AcceptRelocationPayload,
    DownloadRemainsPayloadT,
    DownloadRemainsResponseT,
    DownloadTmcPayloadT,
    DownloadTmcResponseT,
    OrdersDownloadAdmissionsPayloadT,
    OrdersDownloadAdmissionsResponseT,
    OrdersDownloadRelocationsPayloadT,
    OrdersDownloadRelocationsResponseT,
    OrdersDownloadWriteoffsPayloadT,
    OrdersDownloadWriteoffsResponseT,
    RemainsGetListParamsT,
    TmcCreateAdmissionPayload,
    TmcCreatePayload,
    TmcCreateRelocationPayload,
    TmcGetAdmissionResponseT,
    TmcGetAdmissionsListParams,
    TmcGetAdmissionsListResponseT,
    TmcGetListParams,
    TmcGetListResponseT,
    TmcGetRelocationListResponseT,
    TmcGetRelocationResponseT,
    TmcGetRelocationsListParams,
    TmcGetRemainsListResponseT,
    TmcGetUnitsListResponseT,
    TmcGetWriteoffResponseT,
    TmcGetWriteoffsResponseT,
    TmcSecondaryMaterialPayload,
    WriteOffsGetListParams,
    WriteoffPayloadI,
    WriteoffUpdatePayloadI,
} from '../types/tmc.types'

export class TmcRepository {
    getList(
        page: number,
        search?: string | null,
        params?: TmcGetListParams
    ): Promise<AxiosResponse<TmcGetListResponseT>> {
        return http.get('/inventory/api/inventory-items', {
            params: { page, name: search, ...params },
        })
    }

    createTMC(payload: TmcCreatePayload): Promise<AxiosResponse<void>> {
        return http.post('/inventory/api/inventory-items', payload)
    }

    getAdmissionsList(
        params: TmcGetAdmissionsListParams
    ): Promise<AxiosResponse<TmcGetAdmissionsListResponseT>> {
        return http.get('/inventory/api/receipts', { params })
    }

    getAdmission(admission_id: number): Promise<AxiosResponse<TmcGetAdmissionResponseT>> {
        return http.get(`/inventory/api/receipts/${admission_id}`)
    }

    createAdmission(payload: TmcCreateAdmissionPayload): Promise<AxiosResponse<void>> {
        return http.post('/inventory/api/receipts', payload)
    }

    createSecondaryMaterial(payload: TmcSecondaryMaterialPayload): Promise<AxiosResponse<void>> {
        return http.post('/inventory/api/receipts-secondary-material', payload)
    }

    updateAdmission(
        admission_id: number,
        payload: TmcCreateAdmissionPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/receipts/${admission_id}`, payload)
    }

    updateAdmissionSecondary(
        admission_id: number,
        payload: TmcSecondaryMaterialPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/receipts-secondary-material/${admission_id}`, payload)
    }

    completeAdmission(
        receipt_id: number,
        payload: AcceptAdmissionPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/receipts/complete/${receipt_id}`, payload)
    }

    cancelAdmission(receipt_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/receipts/canceled/${receipt_id}`, {})
    }

    getRelocationList(
        params: TmcGetRelocationsListParams
    ): Promise<AxiosResponse<TmcGetRelocationListResponseT>> {
        return http.get('/inventory/api/directions', { params })
    }

    getRelocation(relocation_id: number): Promise<AxiosResponse<TmcGetRelocationResponseT>> {
        return http.get(`/inventory/api/directions/${relocation_id}`)
    }

    createRelocation(payload: TmcCreateRelocationPayload): Promise<AxiosResponse<void>> {
        return http.post('/inventory/api/directions', payload)
    }

    updateRelocation(
        relocation_id: number,
        payload: TmcCreateRelocationPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/directions/${relocation_id}`, payload)
    }

    completeRelocation(
        relocation_id: number,
        payload: AcceptRelocationPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/directions/complete/${relocation_id}`, payload)
    }

    cancelRelocation(relocation_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/directions/canceled/${relocation_id}`, {})
    }

    cancelWriteOff(id: number): Promise<AxiosResponse<void>> {
        return http.put(`/inventory/api/write-offs/canceled/${id}`, { id })
    }

    getUnitsList(page: number, search?: string): Promise<AxiosResponse<TmcGetUnitsListResponseT>> {
        const params = {
            page,
            name: search,
        }

        return http.get('/inventory/api/units', { params })
    }

    getRemainsList(
        params: RemainsGetListParamsT
    ): Promise<AxiosResponse<TmcGetRemainsListResponseT>> {
        return http.get('/inventory/api/inventory-values', { params })
    }

    writeOff(payload: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData('/inventory/api/write-offs', payload)
    }

    updateWriteOff(writeoff_id: number, payload: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData(`/inventory/api/write-offs/${writeoff_id}/update`, payload)
    }

    getWriteOffs(params: WriteOffsGetListParams): Promise<AxiosResponse<TmcGetWriteoffsResponseT>> {
        return http.get('/inventory/api/write-offs', { params })
    }

    getWriteOff(writeoff_id: number): Promise<AxiosResponse<TmcGetWriteoffResponseT>> {
        return http.get(`/inventory/api/write-offs/${writeoff_id}`)
    }

    deleteReceiptItem(receipt_item_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/inventory/api/receipt-items/${receipt_item_id}`)
    }

    deletePackageFile(id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/inventory/api/files/${id}`)
    }

    deleteDirectionItem(direction_item_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/inventory/api/direction-items/${direction_item_id}`)
    }

    deleteWriteoffItem(writeoff_item_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/inventory/api/write-off-items/${writeoff_item_id}`)
    }

    exportExcelAdmissions(
        params: OrdersDownloadAdmissionsPayloadT
    ): Promise<AxiosResponse<OrdersDownloadAdmissionsResponseT>> {
        return http.get('/inventory/api/receipts/export', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    exportExcelRemains(
        params: DownloadRemainsPayloadT
    ): Promise<AxiosResponse<DownloadRemainsResponseT>> {
        return http.get('/inventory/api/accumulation-registers/by-warehouses', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    exportExcelTmc(params: DownloadTmcPayloadT): Promise<AxiosResponse<DownloadTmcResponseT>> {
        return http.get('/inventory/api/accumulation-registers/inventory-item-movements', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    exportExcelRelocations(
        params: OrdersDownloadRelocationsPayloadT
    ): Promise<AxiosResponse<OrdersDownloadRelocationsResponseT>> {
        return http.get('/inventory/api/directions/export', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    exportExcelWriteoffs(
        params: OrdersDownloadWriteoffsPayloadT
    ): Promise<AxiosResponse<OrdersDownloadWriteoffsResponseT>> {
        return http.get('/inventory/api/write-offs/export', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    importInvoices(form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData(`/inventory/api/inventory-values/update-with-excel`, form_data)
    }
}
